import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { EditorStarterItem } from "../../vendor/src/features/remotion-editor/editor/items/item-type";
import { applyTimelineEdit } from "./timeline-editing";
import { itemOwner } from "./group-split";
import { insertTimelineTime } from "./timeline-insert";
import {preserveItemAnimationWindow} from './animation-window';
import {advanceNativeCallLaneBar,isNativeCallLaneBar} from '../../protocol/call-lane-bar';

function rebind(state: EditorState, fromId: string, toId: string): EditorState {
  if (fromId === toId || !state.undoableState.items[fromId]) return state;
  const items = {
    ...state.undoableState.items,
    [toId]: { ...state.undoableState.items[fromId]!, id: toId },
  };
  delete items[fromId];
  for (const item of Object.values(items))
    if (item.type === "group" && item.children.includes(fromId))
      items[item.id] = {
        ...item,
        children: item.children.map((id) => (id === fromId ? toId : id)),
      };
  return {
    ...state,
    selectedItems: [toId],
    activeGroupId: state.activeGroupId === fromId ? toId : state.activeGroupId,
    undoableState: {
      ...state.undoableState,
      items,
      tracks: state.undoableState.tracks.map((track) =>
        track.items.includes(fromId)
          ? {
              ...track,
              items: track.items.map((id) => (id === fromId ? toId : id)),
            }
          : track,
      ),
      transitions: Object.fromEntries(
        Object.entries(state.undoableState.transitions).map(([id, t]) => [
          id,
          {
            ...t,
            sourceItemId: t.sourceItemId === fromId ? toId : t.sourceItemId,
            targetItemId: t.targetItemId === fromId ? toId : t.targetItemId,
          },
        ]),
      ),
    },
  };
}
const sourceStart = (item: EditorStarterItem, fps: number) =>
  item.type === 'solid' && item.wakaCallLaneBar ? item.wakaCallLaneBar.sourceStartFrame/fps :
  item.type === "code" ? -(item.codeClock?.startInSeconds??0) : item.type === "video"
    ? item.videoStartFromInSeconds
    : item.type === "audio"
      ? item.audioStartFromInSeconds
      : item.type === "gif"
        ? item.gifStartFromInSeconds
        : item.type === "webp"
          ? item.webpStartFromInSeconds
          : null;

/** Resize from a frozen gesture baseline so preview events never accumulate timing errors. */
export function resizeTimelineItem(
  state: EditorState,
  id: string,
  side: "left" | "right",
  requestedFrame: number,
): EditorState {
  const item = state.undoableState.items[id],
    owner = itemOwner(state, id);
  if (
    !item ||
    !owner ||
    owner.track.locked ||
    !Number.isSafeInteger(Math.round(requestedFrame)) ||
    !Number.isFinite(state.undoableState.fps) ||
    state.undoableState.fps <= 0
  )
    return state;
  if(item.type==='solid'&&item.wakaCallLaneBar&&!isNativeCallLaneBar(item.wakaCallLaneBar))return state;
  const end = item.from + item.durationInFrames;
  if (
    !Number.isSafeInteger(item.from) ||
    item.from < 0 ||
    !Number.isSafeInteger(item.durationInFrames) ||
    item.durationInFrames < 1
  )
    return state;
  const magnetic = owner.track.magnetic && !owner.parentId;
  let edge =
    side === "left"
      ? Math.min(end - 1, Math.round(requestedFrame))
      : Math.max(item.from + 1, Math.round(requestedFrame));
  if (side === "left" && !magnetic) edge = Math.max(0, edge);
  const shrinking = side === "left" ? edge > item.from : edge < end;
  let next = state;
  if (shrinking) {
    next = applyTimelineEdit(
      state,
      [id],
      edge,
      side === "left" ? "trim-left" : "trim-right",
    );
    if (next === state) return state;
    const surviving =
      next.selectedItems.find(
        (key) =>
          next.undoableState.items[key]?.timelineSourceItemId ===
          (item.timelineSourceItemId ?? id),
      ) ?? next.selectedItems[0];
    if (!surviving) return state;
    next = rebind(next, surviving, id);
    const resized=next.undoableState.items[id];
    if('animations' in item&&item.animations?.length&&resized&&'animations' in resized&&resized.animations){
      const originalAnimations=item.animations;
      // Handle resizing of a fresh effect still follows the clip bounds; explicit Q/W trims retain source phase.
      const animations=resized.animations.map((animation,index)=>{
        const original=originalAnimations[index];
        return original?.type===animation.type&&original.timelineWindow===undefined?{...animation,timelineWindow:undefined}:animation;
      });
      next={...next,undoableState:{...next.undoableState,items:{...next.undoableState.items,[id]:{...resized,animations}}}};
    }
  } else {
    // A group is a container of authored child intervals, not additional source media.
    if (item.type === "group") return state;
    const linked =
      state.isLinkingEnabled &&
      !owner.parentId &&
      owner.track.metadata?.isMainTrack &&
      owner.track.metadata.type === "video";
    const participants: EditorStarterItem[] = [item];
    if (linked)
      for (const track of state.undoableState.tracks) {
        if (
          track.locked ||
          !track.metadata?.isMainTrack ||
          !["caption", "audio"].includes(track.metadata.type) ||
          track.metadata.kind === "bgm"
        )
          continue;
        for (const key of track.items) {
          const peer = state.undoableState.items[key];
          if (
            peer &&
            (side === "left"
              ? peer.from === item.from
              : peer.from + peer.durationInFrames === end)
          )
            participants.push(peer);
        }
      }
    let delta = side === "left" ? item.from - edge : edge - end;
    for (const participant of participants) {
      const start = sourceStart(participant,state.undoableState.fps),
        rate = "playbackRate" in participant ? participant.playbackRate : 1;
      if (side === "left" && start !== null)
        delta = Math.min(
          delta,
          participant.type === 'solid' && participant.wakaCallLaneBar
            ? participant.wakaCallLaneBar.sourceStartFrame
            : Math.floor((start * state.undoableState.fps) / rate),
        );
      if (
        side === "right" &&
        (participant.type === "video" || participant.type === "audio") &&
        !(participant.type === "video" && participant.loop)
      ) {
        const asset = state.undoableState.assets[participant.assetId];
        if (asset && "durationInSeconds" in asset)
          delta = Math.min(
            delta,
            Math.max(
              0,
              Math.floor(
                ((asset.durationInSeconds - (start ?? 0)) / rate) *
                  state.undoableState.fps,
              ) - participant.durationInFrames,
            ),
          );
      }
    }
    if (delta <= 0) return state;
    if (magnetic) {
      const inserted = insertTimelineTime(
        state,
        linked
          ? state.undoableState.tracks.map((track) => track.id)
          : [owner.track.id],
        side === "right" ? end : item.from,
        delta,
        participants.map((participant) => participant.id),
      );
      if (!inserted.ok) return state;
      next = inserted.state;
    }
    const grownItems = { ...next.undoableState.items };
    for (const participant of participants) {
      let grown: EditorStarterItem = {
        // Only previously cut effects have a frozen source window; untouched effects keep normal resize behavior.
        ...preserveItemAnimationWindow(participant,state.undoableState.fps,side==='left'?-delta:0,participant.durationInFrames,true),
        from:
          side === "left" && !magnetic
            ? participant.from - delta
            : participant.from,
        durationInFrames: participant.durationInFrames + delta,
      };
      if (side === "left") {
        grown=advanceNativeCallLaneBar(grown,-delta);
        const seconds = delta / state.undoableState.fps;
        if (grown.type === "video")
          grown = {
            ...grown,
            videoStartFromInSeconds:
              grown.videoStartFromInSeconds - seconds * grown.playbackRate,
          };
        if (grown.type === "audio")
          grown = {
            ...grown,
            audioStartFromInSeconds:
              grown.audioStartFromInSeconds - seconds * grown.playbackRate,
          };
        if (grown.type === "gif")
          grown = {
            ...grown,
            gifStartFromInSeconds:
              grown.gifStartFromInSeconds - seconds * grown.playbackRate,
          };
        if (grown.type === "webp")
          grown = {
            ...grown,
            webpStartFromInSeconds:
              grown.webpStartFromInSeconds - seconds * grown.playbackRate,
          };
        if (grown.type === "captions")
          grown = {
            ...grown,
            captionStartInSeconds: grown.captionStartInSeconds - seconds,
          };
        if ("positionKeyframes" in grown)
          grown.positionKeyframes = grown.positionKeyframes?.map((point) => ({
            ...point,
            frame: point.frame + delta,
          }));
        if ("zoomKeyframes" in grown)
          grown.zoomKeyframes = grown.zoomKeyframes?.map((point) => ({
            ...point,
            frame: point.frame + delta,
          }));
      }
      if (grown.type === "captions")
        grown = {
          ...grown,
          captionEdgeHold: { ...grown.captionEdgeHold, [side]: true },
        };
      grownItems[grown.id] = grown;
    }
    next = {
      ...next,
      selectedItems: [id],
      timelineTimeEditApplied: true,
      undoableState: { ...next.undoableState, items: grownItems },
    };
  }
  const resized = next.undoableState.items[id];
  if (resized?.type === "captions")
    next = {
      ...next,
      undoableState: {
        ...next.undoableState,
        items: {
          ...next.undoableState.items,
          [id]: {
            ...resized,
            captionEdgeHold: { ...resized.captionEdgeHold, [side]: true },
          },
        },
      },
    };
  return next === state ? state : { ...next, timelineTimeEditApplied: true };
}

/** Multi-trim uses one common bounded delta and never processes synced companions twice. */
export function resizeTimelineSelection(
  state: EditorState,
  ids: readonly string[],
  side: "left" | "right",
  delta: number,
): EditorState {
  let targets = [...new Set(ids)].filter((id) => {
    const owner = itemOwner(state, id);
    return state.undoableState.items[id] && owner && !owner.track.locked;
  });
  const main = targets.filter((id) => {
    const owner = itemOwner(state, id);
    return (
      owner &&
      !owner.parentId &&
      owner.track.metadata?.isMainTrack &&
      owner.track.metadata.type === "video"
    );
  });
  if (state.isLinkingEnabled && main.length) targets = main;
  let bounded = Math.round(delta);
  for (const id of targets) {
    const item = state.undoableState.items[id]!;
    const planned = resizeTimelineItem(
      state,
      id,
      side,
      (side === "left" ? item.from : item.from + item.durationInFrames) +
        bounded,
    );
    const after = planned.undoableState.items[id];
    const actual = after
      ? side === "left"
        ? item.durationInFrames - after.durationInFrames
        : after.durationInFrames - item.durationInFrames
      : 0;
    bounded =
      bounded >= 0 ? Math.min(bounded, actual) : Math.max(bounded, actual);
  }
  if (!bounded) return state;
  let next = state;
  for (const id of targets) {
    const item = next.undoableState.items[id];
    if (item)
      next = resizeTimelineItem(
        next,
        id,
        side,
        (side === "left" ? item.from : item.from + item.durationInFrames) +
          bounded,
      );
  }
  return next === state
    ? state
    : {
        ...next,
        selectedItems: targets.filter((id) => next.undoableState.items[id]),
      };
}
